if (!customElements.get('lets-get-real-help')) {
  customElements.define('lets-get-real-help', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.init());
    }
    init() {
      if (this.controller || !this.isConnected) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const successLink = this.querySelector('[data-success-redirect]');
      if (successLink) {
        const destination = new URL(successLink.href, location.href);
        if (['http:', 'https:'].includes(destination.protocol) && destination.href !== location.href) {
          location.assign(destination.href);
          return;
        }
      }
      this.initCounters();
      const state = this.querySelector('[data-state]');
      const city = this.querySelector('[data-city]');
      const store = this.querySelector('[data-store]');
      if (!state || !city || !store) return;
      const records = this.querySelector('[data-store-records]');
      if (records) {
        const locations = Array.from(records.content.querySelectorAll('[data-name]'), item => ({ ...item.dataset }))
          .filter(item => item.name && item.state && item.city);
        const states = [...new Set(locations.map(item => item.state))].sort();
        state.replaceChildren(new Option('Select your state', ''), ...states.map(name => new Option(name, name)));
        this.cityGroups = states.map(name => {
          const group = document.createElement('optgroup');
          group.label = name;
          [...new Set(locations.filter(item => item.state === name).map(item => item.city))].sort()
            .forEach(name => group.append(new Option(name, name)));
          return group;
        });
        this.locations = locations;
      }
      // Keep independent copies so changing a parent never loses its options.
      this.cityGroups ||= Array.from(city.querySelectorAll('optgroup'), group => group.cloneNode(true));
      this.storeGroups ||= Array.from(store.querySelectorAll('optgroup'), group => group.cloneNode(true));
      const fill = (select, groups, value, placeholder) => {
        const selected = select.value;
        select.replaceChildren(new Option(placeholder, ''));
        const group = groups.find(item => item.label === value);
        if (group) select.append(group.cloneNode(true));
        select.disabled = !group;
        if (Array.from(select.options).some(option => option.value === selected)) select.value = selected;
      };
      const updateStore = () => {
        if (!records) return fill(store, this.storeGroups, city.value, 'Select nearest Limelight store');
        const selected = store.value;
        const names = [...new Set(this.locations.filter(item => item.state === state.value && item.city === city.value).map(item => item.name))].sort();
        store.replaceChildren(new Option('Select nearest Limelight store', ''), ...names.map(name => new Option(name, name)));
        store.disabled = names.length === 0;
        if (names.includes(selected)) store.value = selected;
      };
      const updateCity = () => {
        fill(city, this.cityGroups, state.value, 'Select your city');
        updateStore();
      };
      state.addEventListener('change', () => { city.value = ''; store.value = ''; updateCity(); }, options);
      city.addEventListener('change', () => { store.value = ''; updateStore(); }, options);
      updateCity();
      const params = new URLSearchParams(location.search);
      for (const name of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'zc_gad']) {
        const input = this.querySelector(`input[name="${name}"]`);
        if (input) input.value = params.get(name) || '';
      }
      this.querySelectorAll('[data-visit],[data-call]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        this.querySelector('.lgr-help__card').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        this.querySelector(link.hasAttribute('data-call') ? 'input[type="tel"]' : 'input[autocomplete="name"]')?.focus({ preventScroll: true });
      }, options));
      this.querySelector('form')?.addEventListener('submit', event => {
        // Disabled dependent controls are not covered by native required validation.
        if (!state.value || !city.value || !store.value) {
          event.preventDefault();
          state.reportValidity();
        }
      }, options);
    }
    initCounters() {
      const stats = this.querySelector('.lgr-help__stats');
      if (!stats) return;
      this.counters = Array.from(stats.querySelectorAll('strong')).map(element => {
        const text = element.textContent;
        const match = text.match(/\d[\d,]*(?:\.\d+)?/);
        return match ? { element, text, match, target: Number(match[0].replace(/,/g, '')), decimals: (match[0].split('.')[1] || '').length } : null;
      }).filter(Boolean);
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      const finish = () => {
        cancelAnimationFrame(this.counterFrame);
        this.counters.forEach(counter => { counter.element.textContent = counter.text; });
      };
      motion.addEventListener('change', () => { if (motion.matches) finish(); }, { signal: this.controller.signal });
      if (motion.matches) return;
      this.counterObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.counterObserver.disconnect();
        if (motion.matches) return;
        const start = performance.now();
        const update = now => {
          const progress = Math.min(1, (now - start) / 1400);
          const eased = 1 - Math.pow(1 - progress, 3);
          this.counters.forEach(counter => {
            const value = (counter.target * eased).toLocaleString('en-US', { useGrouping: counter.match[0].includes(','), minimumFractionDigits: counter.decimals, maximumFractionDigits: counter.decimals });
            counter.element.textContent = progress === 1 ? counter.text : counter.text.replace(counter.match[0], value);
          });
          if (progress < 1) this.counterFrame = requestAnimationFrame(update);
        };
        this.counterFrame = requestAnimationFrame(update);
      }, { threshold: .25 });
      this.counterObserver.observe(stats);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.counterFrame);
      this.counterObserver?.disconnect();
      this.counters?.forEach(counter => { counter.element.textContent = counter.text; });
      this.controller?.abort();
      this.controller = null;
    }
  });
}
